import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Badge, Title, Accordion, Skeleton, Text, Button } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';

import { GET_APP_CONFIG } from '../../apollo/queries/appConfig';
import { DEFAULT_APP_VAT_PERCENTAGE } from '../../constants/appConfigDefaults';
import { Request, Store } from '../../codegen/graphql';
import ProductHeader from './components/ProductHeader';
import PriceSectionFlat from './components/PriceSectionFlat';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import { calculateMargin } from '../../helpers/calculateMargin';
import {
    getPriceChangeScope,
    getSuggestedStoreIds,
    PRICE_CHANGE_SCOPE,
} from '../../helpers/priceChangeScope';
import {
    getRequestCashbuildCostPrice,
    getRequestEffectiveCostPrice,
    getRequestPnlCostPrice,
    getRequestStoreCostPrice,
    isSingleSuggestedStoreRequest,
} from '../../helpers/requestZonePricing';
import { getProposedBelowCostMessage } from '../../utility/surveyUtils';
import { FuturePlannedPricesCollapsible } from './future-prices/FuturePlannedPricesCollapsible';

type RequestProductInfoRequest = Request & {
    resolvedVatPercentage?: number | null;
    competitorPrice?: number | null;
    priceSurveyType?: string | null;
    priceSurveyLabel?: string | null;
};

type Props = {
    flatRequest: RequestProductInfoRequest | null;
};

function RequestProductInfo({ flatRequest }: Props) {
    const { authUser } = useAuth();
    const hideCostAndMargins = checkUserRole(authUser, [
        UserRole.STORE_MANAGER,
    ]);

    const {
        adZoneStores: adZoneStoresList,
        adZoneLoading,
        currentPrices: currentPricesResult,
        currentPricesLoading,
        hasSharedDataError,
        retrySharedQueries,
    } = useRequestDrawer();

    const { data: appConfigData } = useQuery(GET_APP_CONFIG);
    const appVatPercentage =
        appConfigData?.appConfig?.vatPercentage ?? DEFAULT_APP_VAT_PERCENTAGE;
    const resolvedVatPercentage =
        flatRequest?.resolvedVatPercentage ?? appVatPercentage;

    const benchmarkMargin = flatRequest?.benchmarkMargin ?? 0;

    const currentPricesData = useMemo(
        () =>
            currentPricesResult
                ? { getCurrentPrices: currentPricesResult }
                : undefined,
        [currentPricesResult],
    );

    const adZoneStores: Store[] = useMemo(
        () => adZoneStoresList ?? [],
        [adZoneStoresList],
    );

    const highestCostPrice = useMemo(
        () => (flatRequest ? getRequestEffectiveCostPrice(flatRequest) : null),
        [flatRequest],
    );

    const usesSelectedStoreCost =
        !!flatRequest &&
        getPriceChangeScope(flatRequest) ===
            PRICE_CHANGE_SCOPE.SPECIFIC_STORES &&
        getSuggestedStoreIds(flatRequest).length > 0 &&
        !flatRequest.completed;

    const cashbuildCostPrice = useMemo(() => {
        if (!flatRequest) {
            return null;
        }
        if (usesSelectedStoreCost) {
            return (
                getRequestEffectiveCostPrice(flatRequest) ??
                getRequestCashbuildCostPrice(flatRequest)
            );
        }
        return getRequestCashbuildCostPrice(flatRequest);
    }, [flatRequest, usesSelectedStoreCost]);

    const pnlCostPrice = useMemo(() => {
        if (!flatRequest) {
            return null;
        }
        if (usesSelectedStoreCost) {
            return (
                getRequestEffectiveCostPrice(flatRequest) ??
                getRequestPnlCostPrice(flatRequest)
            );
        }
        return getRequestPnlCostPrice(flatRequest);
    }, [flatRequest, usesSelectedStoreCost]);

    const storeCostPrice = useMemo(
        () => (flatRequest ? getRequestStoreCostPrice(flatRequest) : null),
        [flatRequest],
    );

    const isSharedDataLoading = adZoneLoading || currentPricesLoading;

    const { proposedMargin, isMarginPositive: proposedMarginPositive } =
        useMemo(() => {
            if (!flatRequest || highestCostPrice == null) {
                return {
                    proposedMargin: flatRequest?.proposedMargin ?? 0,
                    isMarginPositive:
                        (flatRequest?.proposedMargin ?? 0) >= benchmarkMargin,
                };
            }
            const result = calculateMargin({
                costPrice: highestCostPrice,
                currentPrice: flatRequest.currentPrice ?? 0,
                proposedPrice: flatRequest.proposedPrice ?? 0,
                benchmarkMargin,
                vatPercentage: resolvedVatPercentage,
            });
            return {
                proposedMargin: result.newMargin,
                isMarginPositive: result.isMarginPositive,
            };
        }, [
            flatRequest,
            highestCostPrice,
            benchmarkMargin,
            resolvedVatPercentage,
        ]);

    const previousCashbuildMarginDisplay = useMemo(() => {
        if (!flatRequest || isSharedDataLoading) return 'N/A';
        const price =
            flatRequest.previousCashbuildPrice ??
            currentPricesData?.getCurrentPrices?.cashbuildPrice ??
            flatRequest.currentPrice;
        if (price != null && cashbuildCostPrice != null) {
            const { currentMargin: margin } = calculateMargin({
                costPrice: cashbuildCostPrice,
                currentPrice: price,
                vatPercentage: resolvedVatPercentage,
            });
            return `${margin.toFixed(2)}%`;
        }
        return 'N/A';
    }, [
        flatRequest,
        currentPricesData,
        cashbuildCostPrice,
        isSharedDataLoading,
        resolvedVatPercentage,
    ]);

    const previousPnlMarginDisplay = useMemo(() => {
        if (!flatRequest || isSharedDataLoading) return 'N/A';
        const price =
            flatRequest.previousPnlPrice ??
            currentPricesData?.getCurrentPrices?.pnlPrice ??
            flatRequest.currentPrice;
        if (price != null && pnlCostPrice != null) {
            const { currentMargin: margin } = calculateMargin({
                costPrice: pnlCostPrice,
                currentPrice: price,
                vatPercentage: resolvedVatPercentage,
            });
            return `${margin.toFixed(2)}%`;
        }
        return 'N/A';
    }, [
        flatRequest,
        currentPricesData,
        pnlCostPrice,
        isSharedDataLoading,
        resolvedVatPercentage,
    ]);

    const stores: Store[] = useMemo(() => {
        if (adZoneStores?.length > 0) {
            return adZoneStores.filter((store: Store) => !store.isClosed);
        }
        if (!flatRequest) return [];
        return [
            {
                id: flatRequest.storeId ?? '',
                name: flatRequest.storeName,
                storeNo: flatRequest.storeNo,
                salesOrgId: flatRequest.salesOrgId,
            } as Store,
        ];
    }, [adZoneStores, flatRequest]);

    const drawerAccordionStyles = {
        control: {
            paddingLeft: 0,
            paddingRight: 0,
            borderRadius: 8,
        },
        item: {
            border: 'none',
        },
    };

    // --- Early return AFTER all hooks ---
    if (!flatRequest) {
        return null;
    }

    const currency = flatRequest.currentPriceCurrency || 'ZAR';
    const isProposedBelowCost =
        !hideCostAndMargins &&
        getProposedBelowCostMessage(
            highestCostPrice,
            flatRequest.proposedPrice,
            resolvedVatPercentage,
        ) !== null;

    const formatPrice = (
        price: number | null | undefined,
        fallback: string = 'N/A',
    ): string => {
        if (typeof price === 'number') {
            return price.toFixed(2);
        }
        return fallback;
    };

    const getPreviousCashbuildPrice = (): string => {
        return formatPrice(
            flatRequest.previousCashbuildPrice ??
                currentPricesData?.getCurrentPrices?.cashbuildPrice ??
                flatRequest.currentPrice,
            'N/A',
        );
    };

    const getPreviousPnlPrice = (): string => {
        return formatPrice(
            flatRequest.previousPnlPrice ??
                currentPricesData?.getCurrentPrices?.pnlPrice ??
                flatRequest.currentPrice,
            'N/A',
        );
    };

    const shouldCollapse = (): boolean => {
        const hasInterimPrice = !!(
            flatRequest.temporaryCashbuildPrice ||
            flatRequest.temporaryPnlPrice ||
            flatRequest.temporaryChangedPrice
        );
        return flatRequest.completed === true || hasInterimPrice;
    };

    const priceSectionProps = {
        competitorPrice: formatPrice(flatRequest.competitorPrice),
        proposedPrice: formatPrice(flatRequest.proposedPrice, '-'),
        costPrice: formatPrice(highestCostPrice),
        storeCostPrice:
            storeCostPrice != null &&
            !isSingleSuggestedStoreRequest(flatRequest)
                ? formatPrice(storeCostPrice)
                : undefined,
        proposedMargin,
        isMarginPositive: proposedMarginPositive,
        currency,
        previousCashbuildPrice: getPreviousCashbuildPrice(),
        previousCashbuildMargin: previousCashbuildMarginDisplay,
        previousPnlPrice: getPreviousPnlPrice(),
        previousPnlMargin: previousPnlMarginDisplay,
        benchmarkMargin: `${parseFloat(
            (benchmarkMargin || 0).toString(),
        ).toFixed(2)}%`,
        stores,
        completed: flatRequest.completed,
        latestStatus: flatRequest.latestStatus,
        hideCostAndMargins,
        isProposedBelowCost,
    };

    const getStatusBadge = () => {
        if (flatRequest.latestStatus === 'Declined') {
            return { color: 'red', label: 'Declined' };
        }
        if (flatRequest.completed) {
            return { color: 'green', label: 'Approved' };
        }
        return { color: 'blue', label: 'Interim Price Suggested' };
    };

    const statusBadge = getStatusBadge();

    const renderPriceContent = () => {
        if (isSharedDataLoading) {
            return (
                <>
                    <div className="pb-2">
                        <Title order={4}>Product Price</Title>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex h-full gap-4">
                            <Skeleton
                                height={40}
                                radius="md"
                                className="flex-1"
                            />
                            <Skeleton
                                height={40}
                                radius="md"
                                className="flex-1"
                            />
                            <Skeleton
                                height={40}
                                radius="md"
                                className="flex-1"
                            />
                            <Skeleton
                                height={40}
                                radius="md"
                                className="flex-1"
                            />
                        </div>
                        <Skeleton height={50} radius="md" />
                    </div>
                </>
            );
        }

        if (hasSharedDataError) {
            return (
                <>
                    <div className="pb-2">
                        <Title order={4}>Product Price</Title>
                    </div>
                    <div className="flex flex-col items-center justify-center gap-3 py-6 rounded-lg bg-[#f4f5f7]">
                        <Text size="sm" c="dimmed">
                            Failed to load price data. Please try again.
                        </Text>
                        <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconRefresh size={14} />}
                            onClick={retrySharedQueries}
                        >
                            Retry
                        </Button>
                    </div>
                </>
            );
        }

        if (shouldCollapse()) {
            return (
                <Accordion styles={drawerAccordionStyles}>
                    <Accordion.Item value="collapsible">
                        <Accordion.Control styles={{}}>
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-5">
                                    <Title order={4}>Product Price</Title>
                                    <Badge
                                        color={statusBadge.color}
                                        variant="light"
                                    >
                                        {statusBadge.label}
                                    </Badge>
                                </div>
                            </div>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <PriceSectionFlat {...priceSectionProps} />
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            );
        }

        return (
            <>
                <div className="pb-2">
                    <Title order={4}>Product Price</Title>
                </div>
                <PriceSectionFlat {...priceSectionProps} />
            </>
        );
    };

    return (
        <>
            <ProductHeader
                sku={flatRequest.productSku || undefined}
                categoryName={flatRequest.productCategory || undefined}
                description={flatRequest.productDescription || undefined}
                isFromPriceSurvey={flatRequest.isFromPriceSurvey ?? false}
                priceSurveyType={flatRequest.priceSurveyType ?? undefined}
                priceSurveyLabel={flatRequest.priceSurveyLabel ?? undefined}
            />

            <FuturePlannedPricesCollapsible
                hasFuturePrice={flatRequest.hasFuturePrice}
                productId={flatRequest.productId}
                withDrawerSurfaces
            />

            <div className="flex flex-col rounded-lg shadow-sm bg-white px-6 py-3 mb-5">
                {renderPriceContent()}
            </div>
        </>
    );
}

export default RequestProductInfo;
